import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import Button from '../components/ui/Button';
import { Input, Select, Textarea } from '../components/ui/Input';

/*
 * Customer terms (migration 070) — Matthew, 18 Sep:
 *
 *   "On the same pricing tab, can there be a button to switch to customer terms. These
 *    should be sorted by customer channel. Eg. Export. This should allow customer terms
 *    to be captured by: Direct or indirect customer ... incoterms (if applicable) mainly
 *    for export customers ... Off Invoice terms ... Rebates Deferred / Miscellaneous /
 *    Warehouse / Ullage / Promotions ... Charges Freight — entered as either fixed "$" or
 *    "%" off list/invoice price, with comments and frequency of payment."
 *
 *   "Also ... some customers set up differently, for example Drakes as a customer, we
 *    supply direct in SA which get warehousing terms. In QLD, they purchase via Metcash
 *    and only get some rebates. I would set these up as both part of the same customer
 *    code for Drakes but need to split these terms out and include supply method."
 *
 * So the unit on screen is not the customer, it is the ROUTE: one card per way we supply
 * an account. Drakes gets two cards under one customer code. That is why the route lives
 * on the terms set rather than on Customer.SupplyRoute, which is single-valued.
 */

/** The eight kinds Matthew named, in the order he named them. */
const KINDS = [
  { key: 'off_invoice',       label: 'Off invoice',        egComment: 'e.g. Warehouse terms', basisFixed: 'list' },
  { key: 'rebate_deferred',   label: 'Rebate — deferred',  egComment: 'e.g. Agreed rebate' },
  { key: 'rebate_misc',       label: 'Rebate — misc',      egComment: 'e.g. Over & above' },
  { key: 'rebate_warehouse',  label: 'Rebate — warehouse', egComment: 'e.g. Warehouse rebate' },
  { key: 'rebate_ullage',     label: 'Rebate — ullage',    egComment: 'e.g. Ullage rebate' },
  { key: 'rebate_promotions', label: 'Rebate — promotions', egComment: 'e.g. Promotional rebate' },
  { key: 'charge_freight',    label: 'Charge — freight',   egComment: 'e.g. Freight charged on all purchases', adhoc: true },
];

const FREQUENCIES = ['monthly', 'quarterly', 'yearly'];
// "Frequency of payment, eg. Adhoc, Monthly, Quarterly or Yearly" — adhoc is on the
// freight line only, which is the one he wrote it against.
const FREQ_LABEL = { adhoc: 'Ad hoc', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };

// The common ones for an Australian FMCG exporter. Free text underneath, because a
// customer will eventually agree something not on this list.
const INCOTERMS = ['EXW', 'FCA', 'FOB', 'CFR', 'CIF', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP'];

const UNSEGMENTED = 'No segment';

function emptyLine(kind) {
  return { kind, valueType: 'pct', value: '', basis: kind === 'off_invoice' ? 'list' : 'invoice', frequency: '', comment: '' };
}

function blankSet(customerCode = '') {
  return {
    termsId: null, customerCode, routeLabel: '', supplyRoute: 'DIRECT', supplyViaCustomerCode: '',
    incoterm: '', startDate: '', endDate: '', notes: '',
    lines: KINDS.map(k => emptyLine(k.key)),
  };
}

/** Merge the saved lines onto the full set of kinds, so the form always shows all eight. */
function linesForEdit(saved) {
  const by = new Map((saved ?? []).map(l => [l.kind, l]));
  return KINDS.map(k => {
    const l = by.get(k.key);
    return l
      ? { kind: k.key, valueType: l.valueType ?? 'pct', value: l.value ?? '', basis: l.basis ?? '', frequency: l.frequency ?? '', comment: l.comment ?? '' }
      : emptyLine(k.key);
  });
}

function fmtValue(line) {
  if (line.value == null || line.value === '') return '—';
  const n = Number(line.value);
  if (!Number.isFinite(n)) return String(line.value);
  return line.valueType === 'pct' ? `${n}%` : `$${n.toFixed(2)}`;
}

const CARD = { background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 12 };
const LBL = { color: 'var(--text-muted)', fontSize: 11, marginBottom: 3 };
const TH = { textAlign: 'left', padding: '6px 8px', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)' };
const TD = { padding: '6px 8px', fontSize: 12, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-soft)' };

export default function CustomerTerms() {
  const apiFetch = useApi();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);   // the terms set being edited, or null
  const [err, setErr] = useState('');
  const [segFilter, setSegFilter] = useState('All');
  const [search, setSearch] = useState('');

  const { data: sets = [], isLoading, error: loadError } = useQuery({
    queryKey: ['customer-terms'],
    queryFn: async () => {
      const r = await apiFetch('/customer-terms');
      if (!r.ok) throw new Error(`Failed to load customer terms: HTTP ${r.status}`);
      return r.json();
    },
  });

  // Every customer, not just those with terms — you add terms to an account that has none.
  const { data: customers = [] } = useQuery({
    queryKey: ['master-customers-all'],
    queryFn: async () => {
      const r = await apiFetch('/master-customers?all=true');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const save = useMutation({
    mutationFn: async (body) => {
      const r = await apiFetch('/customer-terms', { method: 'PUT', body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error || `HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => { setErr(''); setEditing(null); qc.invalidateQueries({ queryKey: ['customer-terms'] }); },
    onError: (e) => setErr(String(e?.message || e)),
  });

  const remove = useMutation({
    mutationFn: async (id) => {
      const r = await apiFetch(`/customer-terms/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error || `HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => { setErr(''); qc.invalidateQueries({ queryKey: ['customer-terms'] }); },
    onError: (e) => setErr(String(e?.message || e)),
  });

  // "These should be sorted by customer channel. Eg. Export." Channel/segment is the
  // grouping, not just a filter — export terms read very differently to grocery ones.
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = new Map();
    for (const t of sets) {
      if (segFilter !== 'All' && (t.segment || UNSEGMENTED) !== segFilter) continue;
      if (q && !(`${t.customerName ?? ''} ${t.customerCode} ${t.routeLabel}`.toLowerCase().includes(q))) continue;
      const seg = t.segment || UNSEGMENTED;
      const arr = out.get(seg) ?? [];
      arr.push(t);
      out.set(seg, arr);
    }
    return [...out.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [sets, segFilter, search]);

  const segments = useMemo(
    () => ['All', ...Array.from(new Set(sets.map(t => t.segment || UNSEGMENTED))).sort()],
    [sets]);

  return (
    <div>
      {loadError && (
        <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 6,
          color: 'var(--danger)', padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
          {String(loadError.message || loadError)}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: 0 }}>Customer Terms</h2>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
            {sets.length} terms {sets.length === 1 ? 'set' : 'sets'} · one per way we supply an account, so a
            customer supplied two ways has two
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Customer or route…"
            style={{ width: 180, fontSize: 12 }} />
          <Select value={segFilter} onChange={e => setSegFilter(e.target.value)} style={{ fontSize: 12 }}>
            {segments.map(s => <option key={s} value={s}>{s === 'All' ? 'All channels' : s}</option>)}
          </Select>
          <Button variant="primary" size="sm" onClick={() => { setErr(''); setEditing(blankSet()); }}>+ Add terms</Button>
        </div>
      </div>

      {err && (
        <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 6,
          color: 'var(--danger)', padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{err}</div>
      )}

      {editing && (
        <TermsEditor
          value={editing}
          customers={customers}
          onChange={setEditing}
          onCancel={() => { setErr(''); setEditing(null); }}
          onSave={() => save.mutate(editing)}
          saving={save.isPending}
        />
      )}

      {isLoading && <div style={{ color: 'var(--text-muted)', padding: 30, textAlign: 'center' }}>Loading customer terms…</div>}

      {!isLoading && sets.length === 0 && !editing && (
        <div style={{ ...CARD, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: 30 }}>
          No customer terms captured yet. Add the first set — name the route the way you would say it
          out loud, like “Direct SA” or “QLD via Metcash”.
        </div>
      )}

      {grouped.map(([seg, rows]) => (
        <div key={seg} style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)',
            fontWeight: 600, marginBottom: 6 }}>{seg}</div>
          {rows.map(t => (
            <TermsCard key={t.termsId} terms={t}
              onEdit={() => { setErr(''); setEditing({ ...t, lines: linesForEdit(t.lines), supplyViaCustomerCode: t.supplyViaCustomerCode ?? '', incoterm: t.incoterm ?? '', startDate: t.startDate ?? '', endDate: t.endDate ?? '', notes: t.notes ?? '' }); }}
              onDelete={() => {
                if (!window.confirm(`Delete the "${t.routeLabel}" terms for ${t.customerName || t.customerCode}?\n\nAny trading term agreements filed against it are kept.`)) return;
                remove.mutate(t.termsId);
              }} />
          ))}
        </div>
      ))}
    </div>
  );
}

function TermsCard({ terms, onEdit, onDelete }) {
  const live = (terms.lines ?? []).filter(l => l.value != null || l.comment);
  const kindLabel = (k) => KINDS.find(x => x.key === k)?.label ?? k;
  return (
    <div style={CARD}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
            {terms.customerName || terms.customerCode}
            <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>{terms.customerCode}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-body)', marginTop: 2 }}>
            <span style={{ background: 'var(--info-soft)', color: 'var(--info)', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
              {terms.routeLabel}
            </span>
            <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>
              {terms.supplyRoute === 'VIA'
                ? `Indirect — via ${terms.supplyViaName || terms.supplyViaCustomerCode}`
                : 'Direct'}
              {terms.incoterm ? ` · ${terms.incoterm}` : ''}
              {terms.startDate ? ` · from ${terms.startDate}` : ''}
              {terms.endDate ? ` to ${terms.endDate}` : ''}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" onClick={onEdit}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={onDelete} style={{ color: 'var(--danger)' }}>Delete</Button>
        </div>
      </div>
      {live.length === 0
        ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No terms captured against this route yet.</div>
        : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>{['Term', 'Value', 'Off', 'Frequency', 'Comment'].map(h => <th key={h} style={TH}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {live.map(l => (
                <tr key={l.termLineId ?? l.kind}>
                  <td style={TD}>{kindLabel(l.kind)}</td>
                  <td style={{ ...TD, fontWeight: 600 }}>{fmtValue(l)}</td>
                  <td style={{ ...TD, color: 'var(--text-muted)' }}>{l.basis === 'invoice' ? 'Invoice' : l.basis === 'list' ? 'List' : '—'}</td>
                  <td style={{ ...TD, color: 'var(--text-muted)' }}>{FREQ_LABEL[l.frequency] ?? '—'}</td>
                  <td style={{ ...TD, color: 'var(--text-muted)' }}>{l.comment || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      {terms.notes && (
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>{terms.notes}</div>
      )}
    </div>
  );
}

function TermsEditor({ value, customers, onChange, onCancel, onSave, saving }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setLine = (kind, k, v) => onChange({
    ...value,
    lines: value.lines.map(l => (l.kind === kind ? { ...l, [k]: v } : l)),
  });
  const isVia = value.supplyRoute === 'VIA';
  const custOptions = useMemo(
    () => [...customers].sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? ''))),
    [customers]);

  return (
    <div style={{ ...CARD, background: 'var(--info-soft)', border: '1px solid var(--info-border)' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--info)', marginBottom: 10 }}>
        {value.termsId ? 'Edit terms' : 'New terms'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10, marginBottom: 12 }}>
        <div>
          <div style={LBL}>Customer</div>
          <Select value={value.customerCode} onChange={e => set('customerCode', e.target.value)} style={{ fontSize: 12, width: '100%' }}>
            <option value="">Choose…</option>
            {custOptions.map(c => <option key={c.customerCode} value={c.customerCode}>{c.name} ({c.customerCode})</option>)}
          </Select>
        </div>
        <div>
          <div style={LBL}>Route name</div>
          <Input value={value.routeLabel} onChange={e => set('routeLabel', e.target.value)}
            placeholder="Direct SA / QLD via Metcash" style={{ fontSize: 12, width: '100%' }} />
        </div>
        <div>
          <div style={LBL}>Supply method</div>
          <Select value={value.supplyRoute} onChange={e => set('supplyRoute', e.target.value)} style={{ fontSize: 12, width: '100%' }}>
            <option value="DIRECT">Direct — we invoice them</option>
            <option value="VIA">Indirect — via a wholesaler</option>
          </Select>
        </div>
        {isVia && (
          <div>
            <div style={LBL}>Supplied via</div>
            <Select value={value.supplyViaCustomerCode} onChange={e => set('supplyViaCustomerCode', e.target.value)} style={{ fontSize: 12, width: '100%' }}>
              <option value="">Choose the wholesaler…</option>
              {custOptions.map(c => <option key={c.customerCode} value={c.customerCode}>{c.name} ({c.customerCode})</option>)}
            </Select>
          </div>
        )}
        <div>
          <div style={LBL}>Incoterm (export)</div>
          <Input list="incoterms" value={value.incoterm} onChange={e => set('incoterm', e.target.value)}
            placeholder="FOB" style={{ fontSize: 12, width: '100%' }} />
          <datalist id="incoterms">{INCOTERMS.map(i => <option key={i} value={i} />)}</datalist>
        </div>
        <div>
          <div style={LBL}>Start date</div>
          <Input type="date" value={value.startDate || ''} onChange={e => set('startDate', e.target.value)} style={{ fontSize: 12, width: '100%' }} />
        </div>
        <div>
          <div style={LBL}>Finish date</div>
          <Input type="date" value={value.endDate || ''} onChange={e => set('endDate', e.target.value)} style={{ fontSize: 12, width: '100%' }} />
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
          <thead>
            <tr>{['Term', '$ or %', 'Value', 'Off', 'Frequency', 'Comment'].map(h => <th key={h} style={TH}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {value.lines.map(l => {
              const meta = KINDS.find(k => k.key === l.kind);
              return (
                <tr key={l.kind}>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{meta?.label ?? l.kind}</td>
                  <td style={TD}>
                    <Select value={l.valueType} onChange={e => setLine(l.kind, 'valueType', e.target.value)} style={{ fontSize: 12 }}>
                      <option value="pct">%</option>
                      <option value="amount">$</option>
                    </Select>
                  </td>
                  <td style={TD}>
                    <Input type="number" step="0.01" value={l.value ?? ''} onChange={e => setLine(l.kind, 'value', e.target.value)}
                      style={{ fontSize: 12, width: 90, textAlign: 'right' }} />
                  </td>
                  <td style={TD}>
                    {/* Off invoice is off LIST by definition — locking it stops the one
                        combination that would be wrong on every reading. */}
                    {meta?.basisFixed
                      ? <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>List</span>
                      : (
                        <Select value={l.basis ?? ''} onChange={e => setLine(l.kind, 'basis', e.target.value)} style={{ fontSize: 12 }}>
                          <option value="list">List</option>
                          <option value="invoice">Invoice</option>
                        </Select>
                      )}
                  </td>
                  <td style={TD}>
                    <Select value={l.frequency ?? ''} onChange={e => setLine(l.kind, 'frequency', e.target.value)} style={{ fontSize: 12 }}>
                      <option value="">—</option>
                      {meta?.adhoc && <option value="adhoc">Ad hoc</option>}
                      {FREQUENCIES.map(f => <option key={f} value={f}>{FREQ_LABEL[f]}</option>)}
                    </Select>
                  </td>
                  <td style={TD}>
                    <Input value={l.comment ?? ''} onChange={e => setLine(l.kind, 'comment', e.target.value)}
                      placeholder={meta?.egComment} style={{ fontSize: 12, width: '100%', minWidth: 200 }} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 10 }}>
        <div style={LBL}>Notes</div>
        <Textarea value={value.notes ?? ''} onChange={e => set('notes', e.target.value)} rows={2}
          placeholder="Anything about this route that the numbers don't say" style={{ fontSize: 12, width: '100%' }} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <Button variant="primary" size="sm" onClick={onSave}
          disabled={saving || !value.customerCode || !value.routeLabel.trim() || (value.supplyRoute === 'VIA' && !value.supplyViaCustomerCode)}>
          {saving ? 'Saving…' : 'Save terms'}
        </Button>
        <Button size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
        {value.supplyRoute === 'VIA' && !value.supplyViaCustomerCode && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)', alignSelf: 'center' }}>
            An indirect route needs the wholesaler we actually invoice.
          </span>
        )}
      </div>
    </div>
  );
}
